// Exportar la estructura de comités (comités, puestos, subcomités y quién los
// ocupa) a PDF, Word y Excel, para revisarla y aprobarla en asambleas y actas.
//
// Recibe un modelo ya armado (ver Comites.jsx → armarEstructura), así este
// archivo no sabe nada de la API: solo dibuja.
//
// modelo = {
//   entidad: 'Federación ...', nivel: 'Federación', generado: '18/10/2026 21:10',
//   comites: [{ nombre, puestos: [Puesto], subcomites: [{ nombre, tipo, puestos: [Puesto] }] }]
// }
// Puesto = { puesto, afiliado, documento, desde, hasta, estado }
//
// Las librerías se cargan solo al exportar (import dinámico), para no
// engordar la carga inicial de la app.

const COLUMNAS = ['Puesto', 'Afiliado', 'Documento', 'Desde', 'Hasta', 'Estado'];
const fila = (p) => [p.puesto, p.afiliado, p.documento, p.desde, p.hasta, p.estado];
// Comités en verde, subcomités/dependencias en azul (igual que en pantalla).
const VERDE = '2E7D32';
const VERDE_SUAVE = 'E8F5E9';
const AZUL = '1565C0';
const AZUL_SUAVE = 'E3F2FD';
const rgb = (hex) => [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));

const titulo = (m) => `Estructura de Comités — ${m.entidad}`;
const subtitulo = (m) => `${m.nivel} · Generado el ${m.generado}`;

// ─────────────────────────────── PDF ───────────────────────────────
export async function generarPdf(m) {
  const { jsPDF } = await import('jspdf');
  const { autoTable } = await import('jspdf-autotable');
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const ancho = doc.internal.pageSize.getWidth();
  const alto = doc.internal.pageSize.getHeight();
  const margen = 14;
  let y = 18;

  // Evita que un título quede solo al final de la página, separado de su tabla.
  const espacio = (mm) => { if (y + mm > alto - 18) { doc.addPage(); y = 18; } };

  doc.setFont('helvetica', 'bold').setFontSize(15).text(titulo(m), margen, y, { maxWidth: ancho - 2 * margen });
  y += doc.splitTextToSize(titulo(m), ancho - 2 * margen).length * 6.5;
  doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(90).text(subtitulo(m), margen, y);
  doc.setTextColor(0);
  y += 8;

  const tabla = (puestos, color, sangria = 0) => {
    autoTable(doc, {
      startY: y,
      head: [COLUMNAS],
      body: puestos.map(fila),
      margin: { left: margen + sangria, right: margen },
      styles: { fontSize: 8.5, cellPadding: 1.6, valign: 'middle' },
      headStyles: { fillColor: rgb(color), textColor: 255, fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 50 - sangria }, 1: { cellWidth: 48 }, 3: { cellWidth: 19 }, 4: { cellWidth: 19 }, 5: { cellWidth: 18 } },
      rowPageBreak: 'avoid',
    });
    y = doc.lastAutoTable.finalY + 6;
  };

  m.comites.forEach((c) => {
    // Banda verde sólida: COMITÉ
    espacio(32);
    doc.setFillColor(...rgb(VERDE)).rect(margen, y - 5, ancho - 2 * margen, 8, 'F');
    doc.setFont('helvetica', 'bold').setFontSize(11.5).setTextColor(255).text(`COMITÉ · ${c.nombre}`, margen + 2.5, y);
    doc.setTextColor(0);
    y += 5;
    tabla(c.puestos, VERDE);
    c.subcomites.forEach((s) => {
      // Banda azul clara con barra azul, con sangría: SUBCOMITÉ / DEPENDENCIA
      const sangria = 6;
      const anchoTexto = ancho - 2 * margen - sangria - 6;
      const lineas = doc.setFont('helvetica', 'bold').setFontSize(9.5).splitTextToSize(`${s.tipo.toUpperCase()} · ${s.nombre}`, anchoTexto);
      const altoBanda = lineas.length * 4.3 + 3;
      espacio(altoBanda + 22);
      doc.setFillColor(...rgb(AZUL_SUAVE)).rect(margen + sangria, y - 4.5, ancho - 2 * margen - sangria, altoBanda, 'F');
      doc.setFillColor(...rgb(AZUL)).rect(margen + sangria, y - 4.5, 1.6, altoBanda, 'F');
      doc.setTextColor(...rgb(AZUL)).text(lineas, margen + sangria + 4, y);
      doc.setTextColor(0);
      y += altoBanda - 1;
      tabla(s.puestos, AZUL, sangria);
    });
    y += 3;
  });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(120);
    doc.text(`${m.entidad} — Estructura de Comités`, margen, alto - 8);
    doc.text(`Página ${i} de ${total}`, ancho - margen, alto - 8, { align: 'right' });
  }
  return doc.output('blob');
}

// ─────────────────────────────── Word ───────────────────────────────
export async function generarWord(m) {
  const {
    Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType,
    HeadingLevel, ShadingType, AlignmentType, Footer, PageNumber,
  } = await import('docx');

  const celda = (texto, colorCabecera) => new TableCell({
    children: [new Paragraph({ children: [new TextRun({ text: texto || '', bold: !!colorCabecera, color: colorCabecera ? 'FFFFFF' : undefined, size: 18 })] })],
    shading: colorCabecera ? { type: ShadingType.CLEAR, color: 'auto', fill: colorCabecera } : undefined,
  });
  const anchos = [26, 24, 16, 11, 11, 12];
  const tabla = (puestos, color, sangria = 0) => new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    indent: sangria ? { size: sangria, type: WidthType.DXA } : undefined,
    columnWidths: anchos.map((a) => a * 96),
    rows: [
      new TableRow({ tableHeader: true, children: COLUMNAS.map((c) => celda(c, color)) }),
      ...puestos.map((p) => new TableRow({ cantSplit: true, children: fila(p).map((v) => celda(v)) })),
    ],
  });

  const hijos = [
    new Paragraph({ heading: HeadingLevel.TITLE, children: [new TextRun({ text: titulo(m) })] }),
    new Paragraph({ children: [new TextRun({ text: subtitulo(m), italics: true, color: '666666' })], spacing: { after: 240 } }),
  ];
  m.comites.forEach((c) => {
    // Comité: banda verde sólida con texto blanco.
    hijos.push(new Paragraph({
      heading: HeadingLevel.HEADING_1, keepNext: true, spacing: { before: 320, after: 80 },
      shading: { type: ShadingType.CLEAR, color: 'auto', fill: VERDE },
      children: [new TextRun({ text: `COMITÉ · ${c.nombre}`, color: 'FFFFFF', bold: true })],
    }));
    hijos.push(tabla(c.puestos, VERDE));
    c.subcomites.forEach((s) => {
      // Subcomité / dependencia: banda azul clara con texto azul, con sangría.
      hijos.push(new Paragraph({
        heading: HeadingLevel.HEADING_2, keepNext: true, spacing: { before: 200, after: 60 }, indent: { left: 400 },
        shading: { type: ShadingType.CLEAR, color: 'auto', fill: AZUL_SUAVE },
        border: { left: { style: 'single', size: 24, color: AZUL, space: 4 } },
        children: [new TextRun({ text: `${s.tipo.toUpperCase()} · ${s.nombre}`, color: AZUL, bold: true })],
      }));
      hijos.push(tabla(s.puestos, AZUL, 400));
    });
  });

  const doc = new Document({
    creator: 'CoopNet',
    title: titulo(m),
    sections: [{
      properties: { page: { margin: { top: 1000, bottom: 1000, left: 1000, right: 1000 } } },
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [new TextRun({ text: `${m.entidad} — Página `, size: 16, color: '888888' }), new TextRun({ children: [PageNumber.CURRENT], size: 16, color: '888888' })],
          })],
        }),
      },
      children: hijos,
    }],
  });
  return Packer.toBlob(doc);
}

// ─────────────────────────────── Excel ───────────────────────────────
export async function generarExcel(m) {
  const ExcelJS = (await import('exceljs')).default;
  const libro = new ExcelJS.Workbook();
  libro.creator = 'CoopNet';
  const hoja = libro.addWorksheet('Estructura', { views: [{ state: 'frozen', ySplit: 4 }] });

  hoja.getCell('A1').value = titulo(m);
  hoja.getCell('A1').font = { bold: true, size: 14 };
  hoja.getCell('A2').value = subtitulo(m);
  hoja.getCell('A2').font = { italic: true, color: { argb: 'FF666666' } };

  const cab = ['Comité', 'Subcomité / Dependencia', 'Tipo', ...COLUMNAS];
  const filaCab = hoja.getRow(4);
  filaCab.values = cab;
  filaCab.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  filaCab.eachCell((c) => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${VERDE}` } }; });

  const pintar = (row, fondo, texto) => {
    row.eachCell({ includeEmpty: true }, (cell) => { cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${fondo}` } }; });
    row.getCell(3).font = { bold: true, color: { argb: `FF${texto}` } };
  };
  m.comites.forEach((c) => {
    c.puestos.forEach((p) => pintar(hoja.addRow([c.nombre, '', 'Comité', ...fila(p)]), VERDE_SUAVE, VERDE));
    c.subcomites.forEach((s) => s.puestos.forEach((p) => pintar(hoja.addRow([c.nombre, s.nombre, s.tipo, ...fila(p)]), AZUL_SUAVE, AZUL)));
  });

  [34, 44, 22, 34, 30, 18, 12, 12, 12].forEach((w, i) => { hoja.getColumn(i + 1).width = w; });
  hoja.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: cab.length } };
  const buf = await libro.xlsx.writeBuffer();
  return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

// ─────────────────────────────── Descarga ───────────────────────────────
export function nombreArchivo(m, ext) {
  const slug = m.entidad.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 80);
  const d = new Date(); // fecha local (no UTC: de noche en Colombia ya sería "mañana")
  const hoyIso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return `Estructura_Comites_${slug}_${hoyIso}.${ext}`;
}

export function descargar(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
